import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import { FormGroup, ControlLabel, DropdownButton, MenuItem } from 'react-bootstrap';
import { find, first, range, padStart, delay } from 'lodash';
import moment from 'moment-timezone';

import DateTimeDisplay from '../../../../components/date-time-display';

// Import Actions
import { requestUpdateOrganization } from '../../../Admin/AdminActions';
import { fetchServerTimeZone } from '../../SetupActions';

// Import Selectors
import { getUser, getUserCurrentOrganization } from '../../../Auth/AuthReducer';


const allFrequency = [
  /*
  {
    label: 'Every 4 Hours',
    value: 4 * 60 * 60,
  },
  {
    label: 'Every 8 Hours',
    value: 8 * 60 * 60,
  },
  {
    label: 'Every 12 Hours',
    value: 12 * 60 * 60,
  },
  */
  {
    label: 'Daily',
    value: 24 * 60 * 60,
  },
  {
    label: 'Every other day',
    value: 2 * 24 * 60 * 60,
  },
  {
    label: 'Every third day',
    value: 3 * 24 * 60 * 60,
  },
  {
    label: 'Weekly',
    value: 7 * 24 * 60 * 60,
  },
];

const hours = range(1, 13).map(value => ({ value, label: value === 0 ? '0' : padStart(value, 2, '0') }));

const minutes = range(0, 59, 15).map(value => ({ value, label: padStart(value, 2, '0') }));

const ampm = [
  {
    label: 'AM',
    value: 'am',
  },
  {
    label: 'PM',
    value: 'pm',
  },
];

class SyncFrequencyComponent extends Component {
  constructor() {
    super();
    this.state = {
      timezone: null,
      now: null,
      flash: false,
    };
    this.timer = 0;
    this.handleSubmit = this.handleSubmit.bind(this);
    this.selectFrequency = this.selectFrequency.bind(this);
    this.selectHour = this.selectHour.bind(this);
    this.selectMinute = this.selectMinute.bind(this);
    this.selectAmpm = this.selectAmpm.bind(this);
    this.updateTimer = this.updateTimer.bind(this);
  }

  componentWillMount() {
    if (this.props.pristine) {
      const { organization } = this.props;
      if (organization) {
        const params = {
          sync_frequency: Math.max(organization.sync_frequency, 24 * 60 * 60),
          sync_hour: organization.sync_hour ? organization.sync_hour % 12 : 0,
          sync_minute: organization.sync_minute,
          sync_ampm: organization.sync_hour >= 12 ? 'pm' : 'am',
        };
        this.props.initialize(params);
      }
    }
    this.fetchTimeZone();
  }

  componentWillUnmount() {
    if (this.timer !== 0) {
      clearInterval(this.timer);
    }
  }

  startTimer() {
    if (this.timer === 0) {
      this.timer = setInterval(this.updateTimer, 1000 * 10);
    }
  }

  updateTimer() {
    this.setState({
      flash: true,
      now: moment().tz(this.state.timezone),
    }, () => {
      delay(() => {
        this.setState({ flash: false });
      }, 500);
    });
  }

  fetchTimeZone() {
    this.props.dispatch(fetchServerTimeZone())
      .then(
        ({ timezone }) => {
          this.setState({ timezone }, () => {
            this.updateTimer();
            this.startTimer();
          });
        },
        () => {

        },
      );
  }

  handleSubmit(data) {
    const { organization } = this.props;
    const h = data.sync_hour || 1;
    const m = data.sync_minute || 0;
    const a = data.sync_ampm || 'am';
    const t = `${h}:${m} ${a}`;
    const dateFormat = 'DD-MM-YYYY';
    const nowStr = moment().format(dateFormat);

    const d = `${nowStr} ${t}`;
    const final = moment.tz(d, `${dateFormat} h:m a`, moment.tz.guess());
    const aDay = 24 * 60 * 60;
    const frequency = data.sync_frequency || aDay;
    let hourValue = final.hours(); // 0 - 23
    let minuteValue = final.minutes(); // 0 - 59
    if (frequency < aDay) {
      hourValue = 1;
      minuteValue = 0;
    }

    const params = {
      sync_frequency: frequency,
      sync_hour: hourValue,
      sync_minute: minuteValue,
      sync_timezone: moment.tz.guess(),
    };
    return this.props.dispatch(requestUpdateOrganization(organization._id, params));
  }

  manualSubmit() {
    const submitter = this.props.handleSubmit(this.handleSubmit);
    delay(submitter, 500); // submits
  }

  selectFrequency(frequency) {
    this.props.change('sync_frequency', frequency);
    this.manualSubmit();
  }

  selectHour(hour) {
    this.props.change('sync_hour', hour);
    this.manualSubmit();
  }

  selectMinute(minute) {
    this.props.change('sync_minute', minute);
    this.manualSubmit();
  }

  selectAmpm(a) {
    this.props.change('sync_ampm', a);
    this.manualSubmit();
  }

  renderForm() {
    const {
      handleSubmit, submitting, selectedSyncFrequency,
      selectedSyncHour, selectedSyncMinute, selectedSyncAmpm,
    } = this.props;
    const { organization } = this.props;

    const selectedFrequency = find(allFrequency, { value: selectedSyncFrequency }) || first(allFrequency);
    const selectedHour = find(hours, { value: selectedSyncHour }) || first(hours);
    const selectedMinute = find(minutes, { value: selectedSyncMinute }) || first(minutes);
    const selectedAmpm = find(ampm, { value: selectedSyncAmpm }) || first(ampm);
    const showTime = selectedSyncFrequency >= 24 * 60 * 60;
    const lastSync = organization && typeof organization.last_synced_date === 'string' ? moment.tz(organization.last_synced_date, moment.tz.guess()) : null;
    const syncTime = organization.last_synced_duration ? moment.duration(organization.last_synced_duration, 'seconds').humanize() : '';
    const nextSync = organization && typeof organization.next_sync_date === 'string' ? moment.tz(organization.next_sync_date, moment.tz.guess()) : null;

    return (
      <form onSubmit={handleSubmit(this.handleSubmit)}>
        <div className="panel panel-default">
          <div className="panel-body">
            <FormGroup>
              <ControlLabel>Sync Frequency:&nbsp;</ControlLabel>
              <DropdownButton
                bsStyle="default"
                bsSize="sm"
                disabled={submitting}
                title={selectedFrequency !== undefined ? selectedFrequency.label : 'Frequency'}
                id="frequency-nav"
                onSelect={this.selectFrequency}
              >
                {allFrequency.map(freq => (
                  <MenuItem
                    active={selectedFrequency !== undefined && selectedFrequency.value === freq.value}
                    key={freq.value}
                    eventKey={freq.value}
                  >
                    {freq.label}
                  </MenuItem>
                  ))}
              </DropdownButton>

              {showTime && <span>
                <ControlLabel>&nbsp;&nbsp;&nbsp;at:&nbsp;&nbsp;&nbsp;</ControlLabel>
                <DropdownButton
                  bsStyle="default"
                  bsSize="sm"
                  disabled={submitting}
                  title={selectedHour !== undefined ? selectedHour.label : '00'}
                  id="hour-nav"
                  onSelect={this.selectHour}
                >
                  {hours.map(h => (
                    <MenuItem
                      active={selectedHour !== undefined && selectedHour.value === h.value}
                      key={h.value}
                      eventKey={h.value}
                    >
                      {h.label}
                    </MenuItem>
                    ))}
                </DropdownButton>
                &nbsp;:&nbsp;
                <DropdownButton
                  bsStyle="default"
                  bsSize="sm"
                  disabled={submitting}
                  title={selectedMinute !== undefined ? selectedMinute.label : '00'}
                  id="minute-nav"
                  onSelect={this.selectMinute}
                >
                  {minutes.map(m => (
                    <MenuItem
                      active={selectedHour !== undefined && selectedMinute.value === m.value}
                      key={m.value}
                      eventKey={m.value}
                    >
                      {m.label}
                    </MenuItem>
                    ))}
                </DropdownButton>
                &nbsp;:&nbsp;
                <DropdownButton
                  bsStyle="default"
                  bsSize="sm"
                  disabled={submitting}
                  title={selectedAmpm !== undefined ? selectedAmpm.label : 'AM'}
                  id="ampm-nav"
                  onSelect={this.selectAmpm}
                >
                  {ampm.map(m => (
                    <MenuItem
                      active={selectedAmpm !== undefined && selectedAmpm.value === m.value}
                      key={m.value}
                      eventKey={m.value}
                    >
                      {m.label}
                    </MenuItem>
                    ))}
                </DropdownButton>
              </span>}
            </FormGroup>
            <ul style={{ padding: '10px' }}>
              <li>Last Sync: <DateTimeDisplay value={lastSync} format="shortWithTime" showTimezone /></li>
              <li>Sync Time: {syncTime}</li>
              <li>Next Sync: <DateTimeDisplay value={nextSync} format="shortWithTime" showTimezone /></li>
              {this.state.now !== null && <li>Server Time: <DateTimeDisplay value={this.state.now} format="shortWithTime" showTimezone timezone={this.state.timezone} /></li>}
            </ul>
          </div>
        </div>
      </form>
    );
  }

  render() {
    const { organization } = this.props;
    const isDemo = organization && organization.is_demo;
    return (
      <div>
        {!isDemo && this.renderForm()}
        {isDemo && <div className="alert alert-warning">
          This is a demo organization. You cannot schedule sync time to it.
        </div>}
      </div>
    );
  }
}

const selector = formValueSelector('organizationForm'); // <-- same as form name

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    user: getUser(state),
    organization: getUserCurrentOrganization(state),
    selectedSyncFrequency: selector(state, 'sync_frequency'),
    selectedSyncHour: selector(state, 'sync_hour'),
    selectedSyncMinute: selector(state, 'sync_minute'),
    selectedSyncAmpm: selector(state, 'sync_ampm'),
  };
}

SyncFrequencyComponent.propTypes = {
  user: PropTypes.object.isRequired,
  organization: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  reset: PropTypes.func,
  error: PropTypes.string,
  pristine: PropTypes.bool.isRequired,
  selectedSyncFrequency: PropTypes.number,
  selectedSyncHour: PropTypes.number,
  selectedSyncMinute: PropTypes.number,
  selectedSyncAmpm: PropTypes.string,
  change: PropTypes.func,
};
SyncFrequencyComponent.contextTypes = {
  router: PropTypes.object,
};

const SyncFrequencyForm = reduxForm({
  form: 'organizationForm',
})(SyncFrequencyComponent);

export default connect(mapStateToProps)(SyncFrequencyForm);
